import { createStore } from 'zustand/vanilla'
import { api, type Itinerary, type TripModes, type TripPlanPoint } from '../services/api'
import { readJson, readText, writeJson, writeText } from '../services/storage'
import { routeFamily } from '../ui/colors'
import { useTransitStore } from './transit'

// Trip-planner state. `activeItinerary` outlives the Plan sheet on purpose: a rider
// who picked a route wants it on the map while walking, so only starting a new
// search or the explicit "Clear route" button clears it — not closing the sheet.
//
// The drawn itinerary and the map's TRIP-FOCUS mode are separate, though: focus
// (hiding every line but the trip's) belongs to the moment the rider is reading the
// plan or riding it. Backing out of the itinerary or closing the sheet releases focus
// via setMapFocus(false) — the whole system comes back with the trip still drawn on it.

/** An origin/destination the user picked. Coordinates are always present; `id` only
 *  for stations (routing by station id beats coords: no walk-radius guessing). */
export interface PlanPlace {
  kind: 'current' | 'station' | 'address'
  id?: string
  name: string
  lat: number
  lon: number
}

export interface RecentTrip {
  origin: PlanPlace
  destination: PlanPlace
  atMs: number
}

const RECENTS_KEY = 'transit_recent_trips'
const MAX_RECENTS = 8
const MODES_KEY = 'transit_plan_modes'
const EXPRESS_BUS_KEY = 'transit_plan_express_bus'

function loadModes(): TripModes {
  return readText(MODES_KEY, 'subway,bus', decodeModes)
}

function decodeModes(value: string): TripModes | undefined {
  return value === 'subway' || value === 'bus' || value === 'subway,bus' ? value : undefined
}

/** Express buses are allowed unless the rider turned them off (they cost extra). */
function loadExpressBus(): boolean {
  return readText(EXPRESS_BUS_KEY, true, (value) => value !== 'false')
}

function loadRecents(): RecentTrip[] {
  return readJson(RECENTS_KEY, [], decodeRecents)
}

function decodeRecents(value: unknown): RecentTrip[] | undefined {
  if (!Array.isArray(value)) return undefined
  return value.filter((x): x is RecentTrip => {
    const t = x as RecentTrip
    return !!t && typeof t === 'object' &&
      !!t.origin && typeof t.origin.name === 'string' && typeof t.origin.lat === 'number' &&
      !!t.destination && typeof t.destination.name === 'string' && typeof t.destination.lat === 'number'
  })
}

function persistRecents(recents: RecentTrip[]): void {
  writeJson(RECENTS_KEY, recents)
}

/** Mirror the active itinerary's ride routes into the transit store, which puts the
 *  map in trip-focus mode: only those lines (full route, so the rider can board any
 *  vehicle of the route) and the itinerary's walking legs stay visible. */
function syncTripRoutes(it: Itinerary | null): void {
  // routeFamily, not the raw leg id: the planner rides GTFS route_ids, so an express leg
  // arrives as '7X'. Focusing on that alone hid the 7's route line and every local 7 the
  // rider could actually board.
  const ids = it ? it.legs.flatMap((l) => (l.mode === 'walk' ? [] : routeFamily(l.routeId))) : []
  useTransitStore.getState().setTripRouteIds(ids)
}

function toPoint(place: PlanPlace): TripPlanPoint {
  return place.kind === 'station' && place.id
    ? { stopId: place.id }
    : { lat: place.lat, lon: place.lon }
}

/** Same station id, or same coordinate to ~10 m — close enough to be "the same place". */
function samePlace(a: PlanPlace, b: PlanPlace): boolean {
  if (a.kind === 'station' && b.kind === 'station') return a.id === b.id
  return Math.abs(a.lat - b.lat) < 1e-4 && Math.abs(a.lon - b.lon) < 1e-4
}

export type PlanStatus = 'idle' | 'loading' | 'results' | 'error'

interface TripPlanState {
  origin: PlanPlace | null
  destination: PlanPlace | null
  /** Epoch seconds to depart at; null = leave now. */
  departAt: number | null
  /** Transit modes the journey may ride; persisted across sessions. */
  modes: TripModes
  /** Whether MTA express buses may be ridden; persisted across sessions. */
  expressBus: boolean
  status: PlanStatus
  itineraries: Itinerary[]
  realtimeApplied: boolean
  /** Human-readable failure ('' while status !== 'error'). */
  errorMessage: string
  /** The itinerary drawn on the map, if any. */
  activeItinerary: Itinerary | null
  recentTrips: RecentTrip[]

  setOrigin: (p: PlanPlace | null) => void
  setDestination: (p: PlanPlace | null) => void
  setDepartAt: (epoch: number | null) => void
  setModes: (m: TripModes) => void
  setExpressBus: (on: boolean) => void
  swapEnds: () => void
  search: () => Promise<void>
  setActiveItinerary: (it: Itinerary | null) => void
  /** Turn the map's trip-focus mode on/off without touching the drawn itinerary. */
  setMapFocus: (on: boolean) => void
  clearResults: () => void
}

export const useTripPlanStore = createStore<TripPlanState>((set, get) => ({
  origin: null,
  destination: null,
  departAt: null,
  modes: loadModes(),
  expressBus: loadExpressBus(),
  status: 'idle',
  itineraries: [],
  realtimeApplied: false,
  errorMessage: '',
  activeItinerary: null,
  recentTrips: loadRecents(),

  setOrigin: (p) => set({ origin: p, status: 'idle', itineraries: [] }),
  setDestination: (p) => set({ destination: p, status: 'idle', itineraries: [] }),
  setDepartAt: (epoch) => set({ departAt: epoch }),
  setModes: (m) => {
    writeText(MODES_KEY, m)
    set({ modes: m })
    // A mode change invalidates the current answer; re-search right away when a
    // trip is already on screen.
    const { origin, destination, status } = get()
    if (origin && destination && status !== 'idle') void get().search()
  },
  setExpressBus: (on) => {
    writeText(EXPRESS_BUS_KEY, String(on))
    set({ expressBus: on })
    const { origin, destination, status } = get()
    if (origin && destination && status !== 'idle') void get().search()
  },
  swapEnds: () => set((s) => ({ origin: s.destination, destination: s.origin, status: 'idle', itineraries: [] })),

  search: async () => {
    const { origin, destination, departAt, modes, expressBus } = get()
    if (!origin || !destination) return
    syncTripRoutes(null)
    set({ status: 'loading', errorMessage: '', activeItinerary: null })
    try {
      const res = await api.planTrip(toPoint(origin), toPoint(destination), departAt ?? undefined, modes, expressBus)
      set((s) => ({
        status: 'results',
        itineraries: res.itineraries,
        realtimeApplied: res.realtimeApplied,
        recentTrips: pushRecent(s.recentTrips, origin, destination),
      }))
    } catch (err) {
      set({
        status: 'error',
        itineraries: [],
        errorMessage: err instanceof Error && err.message.includes('404')
          ? 'No transit stops near one of those points.'
          : 'Trip planning is unavailable right now. Try again in a moment.',
      })
    }
  },

  setActiveItinerary: (it) => {
    syncTripRoutes(it)
    set({ activeItinerary: it })
  },

  setMapFocus: (on) => syncTripRoutes(on ? get().activeItinerary : null),

  clearResults: () => set({ status: 'idle', itineraries: [], errorMessage: '' }),
}))

function pushRecent(recents: RecentTrip[], origin: PlanPlace, destination: PlanPlace): RecentTrip[] {
  // "Current location" is meaningless to replay later; only concrete places recur.
  if (origin.kind === 'current' && destination.kind === 'current') return recents
  const next = recents.filter((r) => !(samePlace(r.origin, origin) && samePlace(r.destination, destination)))
  next.unshift({ origin, destination, atMs: Date.now() })
  return next.slice(0, MAX_RECENTS)
}

useTripPlanStore.subscribe((state, prev) => {
  if (state.recentTrips !== prev.recentTrips) persistRecents(state.recentTrips)
})
